import styled, {css} from "styled-components";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {ConsentBox, ConsentRow, ConsentText} from "pay/ImmediateDeliveryConsent.styles";
import {brand, fontFamilies, radii} from "theme/tokens";
import {eyebrowTextCss} from "theme/typography";

/**
 * Externalized styles for {@link ./SponsorPage} (ui#496). Colours are `theme.tokens` reads throughout,
 * so the whole booking flow follows light and dark without a second stylesheet.
 *
 * The page is a numbered narrative rather than a form in a box: a stranger with a credit card is being
 * asked to choose a look, upload art, pick dates and pay, and each of those is a decision that deserves
 * its own surface.
 */

/** Editorial page head. Wider measure than the prose pages — the sections below it are card grids. */
export const Hero = styled.header`
	margin-bottom: 40px;
`;

export const HeroLede = styled.p`
	margin: 16px 0 0;
	max-width: 620px;
	font-size: 16px;
	line-height: 1.6;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** One numbered step of the flow. */
export const Section = styled.section`
	margin-bottom: 36px;
`;

export const SectionHead = styled.div`
	margin-bottom: 14px;
`;

/** The mono step label ("Choose a look"), numbered so the flow reads as a sequence. */
export const StepLabel = styled.span`
	${eyebrowTextCss};
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
	font-weight: 700;
	letter-spacing: 0.1em;
`;

export const StepNumber = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: ${({theme}) => theme.tokens.accentTint};
	font-size: 10px;
	font-weight: 700;
`;

export const SectionTitle = styled.h2`
	margin: 8px 0 0;
	font-size: clamp(21px, 2.6vw, 26px);
	font-weight: 700;
	letter-spacing: -0.015em;
	line-height: 1.2;
	color: ${({theme}) => theme.tokens.ink};
`;

export const SectionLede = styled.p`
	margin: 8px 0 0;
	max-width: 620px;
	font-size: 14px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** A plain token surface for the sections that are not already a grid of their own cards. */
export const Panel = styled.div`
	width: 100%;
	box-sizing: border-box;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.card};
	padding: 18px;
`;

/* ---------- creative ---------- */

export const AssetRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	align-items: flex-start;
`;

export const AssetTile = styled.figure`
	position: relative;
	width: 140px;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

export const AssetImage = styled.img`
	width: 100%;
	aspect-ratio: 16 / 10;
	object-fit: cover;
	border-radius: ${radii.panel};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.field};
`;

export const AssetControls = styled.figcaption`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 4px;
`;

/** Small square control for reorder/remove. 32px is the floor a thumb can hit reliably. */
export const TileButton = styled.button.attrs({type: "button"})`
	width: 32px;
	height: 32px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: ${radii.chip};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 13px;
	line-height: 1;
	cursor: pointer;
	transition: 0.14s;

	&:hover:not(:disabled) {
		border-color: ${({theme}) => theme.tokens.blue};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:disabled {
		color: ${({theme}) => theme.tokens.ink3};
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const RemoveButton = styled(TileButton)`
	color: ${brand.error};

	&:hover:not(:disabled) {
		border-color: ${brand.error};
		color: ${brand.error};
	}
`;

/**
 * The add-an-image control. A `<label>` wrapping a hidden `<input type="file">`, which is the only way
 * to restyle a file picker at all — the native control's button is not styleable.
 */
export const AddAssetLabel = styled.label<{$disabled: boolean}>`
	width: 140px;
	aspect-ratio: 16 / 10;
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 6px;
	border-radius: ${radii.panel};
	border: 1.5px dashed ${({theme}) => theme.tokens.fieldLine};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12.5px;
	font-weight: 700;
	text-align: center;
	padding: 8px;
	box-sizing: border-box;
	cursor: ${({$disabled}) => ($disabled ? "not-allowed" : "pointer")};
	opacity: ${({$disabled}) => ($disabled ? 0.55 : 1)};
	transition: 0.16s;

	&:hover {
		border-color: ${({theme, $disabled}) => ($disabled ? theme.tokens.fieldLine : theme.tokens.blue)};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** Hidden from view, kept in the accessibility tree and the tab order (never `display: none`). */
export const HiddenFileInput = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	white-space: nowrap;
	clip-path: inset(50%);
`;

export const AssetCounter = styled.p`
	margin: 12px 0 0;
	font-size: 12.5px;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const EmptyNote = styled.p`
	margin: 0 0 12px;
	font-size: 13.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;


/* ---------- form ---------- */

export const Fields = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 18px;
`;

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/** Spans the whole grid for the fields that deserve the full measure (headline, link). */
export const WideField = styled(Field)`
	grid-column: 1 / -1;
`;

export const FieldHint = styled.span`
	padding-inline-start: 2px;
	font-size: 12.5px;
	line-height: 1.4;
	/* ink2, not ink3: ink3 is the disabled/decorative tier and fails WCAG AA as readable text (ui#302). */
	color: ${({theme}) => theme.tokens.ink2};
`;

export const FieldError = styled.p`
	margin: 0;
	padding-inline-start: 2px;
	font-size: 12.5px;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/* ---------- review ---------- */

export const SummaryList = styled.dl`
	margin: 0;
	display: grid;
	grid-template-columns: auto 1fr;
	gap: 10px 20px;
`;

export const SummaryTerm = styled.dt`
	font-size: 13px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const SummaryValue = styled.dd`
	margin: 0;
	font-size: 13.5px;
	font-weight: 700;
	text-align: right;
	color: ${({theme}) => theme.tokens.ink};
`;

export const TotalRow = styled.div`
	margin-top: 16px;
	padding-top: 14px;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
`;

export const TotalLabel = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const TotalAmount = styled.span`
	font-size: clamp(24px, 3vw, 30px);
	font-weight: 700;
	letter-spacing: -0.02em;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink};
`;

export const PerDayNote = styled.span`
	display: block;
	font-size: 12.5px;
	font-weight: 400;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const Note = styled.p`
	margin: 14px 0 0;
	font-size: 12.5px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** Whole-request failure, above the CTA where it cannot be missed on the way to pressing it. */
export const RequestError = styled.p`
	margin: 16px 0 0;
	padding: 10px 12px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${brand.error};
	color: ${({theme}) => theme.tokens.errorInk};
	font-size: 13.5px;
	line-height: 1.5;
`;

export const SubmitRow = styled.div`
	margin-top: 18px;
	display: flex;
	justify-content: flex-end;
`;

const buttonBase = css`
	height: 48px;
	padding: 0 24px;
	border-radius: ${radii.control};
	font-family: ${fontFamilies.sans};
	font-size: 15px;
	font-weight: 700;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	cursor: pointer;
	transition: 0.16s;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	&:disabled {
		cursor: not-allowed;
	}
`;

export const SubmitButton = styled.button.attrs({type: "submit"})`
	${buttonBase};
	border: none;
	background: ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.blueInk};

	&:hover:not(:disabled) {
		background: ${({theme}) => theme.tokens.blued};
	}

	&:disabled {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
	}

	@media (max-width: 600px) {
		width: 100%;
	}
`;

/** Skeleton stand-in while the availability calendar loads. */
export const LoadingPanel = styled(Panel)`
	min-height: 180px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 13.5px;
`;

/* ---------- categories and wallpaper options (kitecorp/transferify-ui#1868) ---------- */

/**
 * One category of treatments: a heading, a line saying what the category IS, and its own grid of cards.
 *
 * The two groups are stacked rather than tabbed. A tab would hide half the catalogue behind a control
 * nobody told the buyer to press, and the difference between the two — art behind the card versus the
 * whole page — is exactly what a first-time advertiser is choosing between.
 */
export const CategoryGroup = styled.section`
	& + & {
		margin-top: 26px;
	}
`;

export const CategoryHead = styled.div`
	margin-bottom: 12px;
`;

/**
 * An `h2`, matching the `SectionTitle` every other step carries.
 *
 * Step 1 has no section title of its own — its head is the numbered step label, which is a `span` — so
 * an `h3` here would put the first two headings on the page under no parent at all, and a reader
 * navigating by heading would meet "Backdrop" before anything that holds it.
 */
export const CategoryName = styled.h2`
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const CategoryLede = styled.p`
	margin: 4px 0 0;
	max-width: 620px;
	font-size: 13px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The wallpaper settings, in the same surface as the picker they belong to. */
export const OptionsPanel = styled(Panel)`
	margin-top: 20px;
`;

/** The live poster preview above the settings that change it. */
export const OptionsPreview = styled.div`
	margin-bottom: 20px;
`;

/* ---------- Advertising Terms acceptance (kitecorp/transferify-ui#2602) ---------- */

/**
 * The acceptance row above the submit button: the box and its sentence, the checkout consent's own
 * primitives (`pay/ImmediateDeliveryConsent.styles`) rather than a second drawing of the same control.
 * It sits under the review notes as one more line of the panel, so it carries the notes' top margin
 * and no rule of its own — the panel border is the frame here, not a border-top.
 */
export const TermsConsentRow = styled(ConsentRow)`
	margin-top: 14px;
`;

/*
 * Re-exported under this page's own names, unchanged, rather than imported from `pay/` by the component.
 * The acceptance row is deliberately the checkout consent's control, so its box and text carry no rules
 * of their own; the aliases exist so `SponsorPage.tsx` takes every style it renders from its one sibling
 * styles file, which is the convention, instead of reaching into another feature's styles module.
 */
export const TermsConsentBox = ConsentBox;

export const TermsConsentText = ConsentText;

/**
 * The link inside the acceptance sentence and beside "Pay now". A real anchor to the document, opened
 * in a new tab so the half-filled form underneath is not lost; the padding/negative-margin pair grows
 * the target past WCAG 2.5.8's 24px without moving the sentence, as `HowItWorksLink` does.
 */
export const TermsLink = styled(RouterLink)`
	display: inline;
	padding: 5px 3px;
	margin: -5px -3px;
	color: ${({theme}) => theme.tokens.blue};
	text-decoration: underline;
	text-underline-offset: 2px;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		border-radius: 3px;
	}
`;
